/**
 * Service d'exécution des optimisations
 * Implémente les recommandations d'optimisation dans le classeur Excel
 * Fonctionne sur TOUTES les feuilles du classeur, pas seulement la sélection
 * Retourne les détails des modifications effectuées
 */

/* global Excel */

import type { OptimizationPriority } from '@excel-ai-studio/shared';

export interface OptimizationResult {
  success: boolean;
  message: string;
  affectedCells?: number;
  details?: string;
  changes?: Array<{ sheet: string; cell: string; before: string; after: string; type?: 'value' | 'fill' }>;
  restored?: number;
  errors: string[];
}

/**
 * Interface pour tracker les changements et permettre le rollback
 */
interface StateSnapshot {
  timestamp: number;
  actionLabel: string;
  changes: Array<{ sheet: string; cell: string; before: string; after: string; type?: 'value' | 'fill' }>;
}

const snapshotStack: StateSnapshot[] = [];
const MAX_SNAPSHOTS = 10;
let targetSheets: Set<string> | null = null;

/**
 * Capture l'état actuel pour permettre le rollback
 */
function captureState(actionLabel: string): StateSnapshot {
  return {
    timestamp: Date.now(),
    actionLabel,
    changes: [],
  };
}

/**
 * Enregistre un changement pour le tracking
 */
function recordChange(
  snapshot: StateSnapshot,
  sheet: string,
  cell: string,
  before: string,
  after: string,
  type: 'value' | 'fill' = 'value'
): void {
  snapshot.changes.push({ sheet, cell, before, after, type });
}

/**
 * Pousse un snapshot dans la pile (limité à MAX_SNAPSHOTS)
 */
function pushSnapshot(snapshot: StateSnapshot): void {
  if (snapshot.changes.length === 0) return;
  snapshotStack.push(snapshot);
  while (snapshotStack.length > MAX_SNAPSHOTS) {
    snapshotStack.shift();
  }
}

/**
 * Effectue un rollback des changements en restaurant les valeurs originales
 */
async function rollbackChanges(snapshot: StateSnapshot): Promise<void> {
  if (snapshot.changes.length === 0) return;

  return new Promise((resolve, reject) => {
    Excel.run(async (context) => {
      try {
        for (const change of snapshot.changes) {
          // Deleted columns cannot be restored — skip them
          if (change.before === 'column' && change.after === 'deleted') continue;

          const sheetName = change.sheet;
          const sheet = context.workbook.worksheets.getItem(sheetName);
          const cellRef = change.cell.includes('!') ? change.cell.split('!')[1] : change.cell;
          const cell = sheet.getRange(cellRef);

          if (change.type === 'fill') {
            if (change.before === 'none') {
              cell.format.fill.clear();
            } else {
              cell.format.fill.color = change.before;
            }
          } else {
            if (change.before.startsWith('=')) {
              cell.formulas = [[change.before]];
            } else {
              const num = Number(change.before);
              if (change.before !== '' && !isNaN(num)) {
                cell.values = [[num]];
              } else {
                cell.values = [[change.before]];
              }
            }
          }
        }

        await context.sync();
        resolve();
      } catch (error) {
        reject(error);
      }
    }).catch(reject);
  });
}

/**
 * Retourne true s'il y a des changements annulables
 */
export function hasUndoableChanges(): boolean {
  return snapshotStack.length > 0;
}

/**
 * Retourne l'historique des actions annulables
 */
export function getUndoHistory(): Array<{ label: string; count: number; timestamp: number }> {
  return snapshotStack.map((s) => ({
    label: s.actionLabel,
    count: s.changes.length,
    timestamp: s.timestamp,
  }));
}

/**
 * Preview optimization without applying modifications.
 * Lightweight compatibility helper used by audit page.
 */
export async function previewOptimization(priority: OptimizationPriority): Promise<OptimizationResult> {
  return {
    success: true,
    message: `Prévisualisation prête pour: ${priority.title}`,
    affectedCells: 0,
    details: 'Aucune modification n’a été appliquée.',
    changes: [],
    errors: [],
  };
}

/**
 * Roll back last tracked optimization changes.
 */
export async function rollbackOptimizationChanges(
  _context?: unknown
): Promise<OptimizationResult> {
  if (snapshotStack.length === 0) {
    return {
      success: false,
      message: 'Aucun changement à annuler.',
      affectedCells: 0,
      restored: 0,
      errors: [],
      changes: [],
    };
  }

  try {
    const snapshot = snapshotStack.pop()!;
    await rollbackChanges(snapshot);
    return {
      success: true,
      message: `Annulation de "${snapshot.actionLabel}" effectuée (${snapshot.changes.length} modification(s) restaurée(s)).`,
      affectedCells: snapshot.changes.length,
      restored: snapshot.changes.length,
      errors: [],
      changes: snapshot.changes,
    };
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
    return {
      success: false,
      message: `Échec de l'annulation: ${errorMsg}`,
      affectedCells: 0,
      restored: 0,
      errors: [errorMsg],
      changes: [],
    };
  }
}

/**
 * Exécute une priorité d'optimisation
 */
export async function implementOptimization(priority: OptimizationPriority): Promise<OptimizationResult> {
  const snapshot = captureState(priority.title);

  const target = (priority.affectedAreas ?? [])
    .map((s) => String(s ?? '').trim())
    .filter(Boolean)
    .map((s) => s.toLowerCase());
  targetSheets = target.length > 0 ? new Set(target) : null;

  try {
    // Vérifier que Excel est disponible
    if (typeof Excel === 'undefined') {
      console.error('Excel not available');
      throw new Error('Excel context not available - Office.js not loaded');
    }

    console.log('Optimisation demandée:', priority.title);
    const actionKey = priority.actionKey;
    const title = priority.title.toLowerCase();

    let result: OptimizationResult;

    // Dispatch on actionKey first (reliable), fall back to title matching
    if (actionKey === 'clean_missing_data') {
      result = await cleanMissingData();
    } else if (actionKey === 'replace_volatile_functions') {
      result = await replaceVolatileFunctions();
    } else if (actionKey === 'simplify_formula_chains') {
      result = await simplifyFormulaChains();
    } else if (actionKey === 'limit_full_column_formulas') {
      result = await limitFullColumnFormulas();
    } else if (actionKey === 'consolidate_duplicate_calculations') {
      result = await consolidateDuplicateCalculations();
    } else if (actionKey === 'remove_empty_columns') {
      result = await removeEmptyColumns();
    } else if (actionKey === 'improve_data_quality') {
      result = await improveDataQuality();
    } else if (title.includes('risques critiques')) {
      result = await cleanMissingData();
    } else if (title.includes('haute gravité') || title.includes('haute')) {
      result = await replaceVolatileFunctions();
    } else if (title.includes('standardiser') || title.includes('formule')) {
      result = await simplifyFormulaChains();
    } else if (title.includes('optimisation majeure') || title.includes('majeure')) {
      result = await limitFullColumnFormulas();
    } else if (title.includes('réglage') || title.includes('performance')) {
      result = await consolidateDuplicateCalculations();
    } else if (title.includes('volatile')) {
      result = await replaceVolatileFunctions();
    } else if (title.includes('colonne complète') || title.includes('full column')) {
      result = await limitFullColumnFormulas();
    } else if (title.includes('chaîne')) {
      result = await simplifyFormulaChains();
    } else if (title.includes('doublon') || title.includes('duplicate')) {
      result = await consolidateDuplicateCalculations();
    } else if (title.includes('données') && title.includes('manquante')) {
      result = await cleanMissingData();
    } else if (title.includes('colonne') && title.includes('vide')) {
      result = await removeEmptyColumns();
    } else if (title.includes('qualité') && title.includes('données')) {
      result = await improveDataQuality();
    } else {
      result = {
        success: false,
        message: `Implémentation non disponible pour: ${priority.title}`,
        errors: [],
      };
    }

    // Enregistrer les changements dans le snapshot
    if (result.changes) {
      for (const change of result.changes) {
        recordChange(snapshot, change.sheet, change.cell, change.before, change.after, change.type ?? 'value');
      }
    }

    // Sauvegarder le snapshot dans la pile pour permettre l'annulation
    pushSnapshot(snapshot);

    // Force recalculation so a subsequent scan uses updated values.
    try {
      await Excel.run(async (ctx) => {
        const app: any = ctx.workbook.application as any;
        if (typeof app?.calculate === 'function') {
          app.calculate();
        }
        await ctx.sync();
      });
    } catch {
      // ignore recalculation failures
    }

    return result;
  } catch (error) {
    console.error('Erreur d\'implémentation:', error);
    
    // Rollback en cas d'erreur
    if (snapshot.changes.length > 0) {
      try {
        await rollbackChanges(snapshot);
      } catch (rollbackError) {
        console.error('Erreur lors du rollback:', rollbackError);
      }
    }

    const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
    throw new Error(`Erreur lors de l'implémentation: ${errorMsg}`);
  } finally {
    targetSheets = null;
  }
}

/**
 * Remplace les fonctions volatiles par des valeurs statiques
 * Scanne TOUTES les feuilles du classeur
 */
async function replaceVolatileFunctions(): Promise<OptimizationResult> {
  return new Promise((resolve, reject) => {
    Excel.run(async (context) => {
      try {
        console.log('📊 Scan de toutes les feuilles...');
        const sheets = context.workbook.worksheets;
        sheets.load('items');
        await context.sync();

        let totalCount = 0;
        const allChanges: Array<{ sheet: string; cell: string; before: string; after: string }> = [];
        const changesLimit = 80;

        for (let sheetIdx = 0; sheetIdx < sheets.items.length; sheetIdx++) {
          const sheet = sheets.items[sheetIdx];
          console.log(`  Feuille ${sheetIdx + 1}/${sheets.items.length}: ${sheet.name}`);

          if (targetSheets && !targetSheets.has(String(sheet.name).toLowerCase())) {
            continue;
          }

          try {
            const usedRange = sheet.getUsedRange();
            usedRange.load('formulas');
            usedRange.load('values');
            usedRange.load('address');
            await context.sync();

            const formulas = usedRange.formulas as unknown[][];
            const valuesGrid = usedRange.values as unknown[][]; // computed values

            for (let i = 0; i < formulas.length; i++) {
              for (let j = 0; j < formulas[i].length; j++) {
                const formulaCell = formulas[i]?.[j];
                const formula = typeof formulaCell === 'string' ? formulaCell : '';
                if (!formulaCell || typeof formulaCell !== 'string' || !formula.startsWith('=')) continue;

                // Detect known volatile functions (EN + FR common names)
                const isVolatile =
                  /NOW\(\)|MAINTENANT\(\)/i.test(formula) ||
                  /TODAY\(\)|AUJOURDHUI\(\)/i.test(formula) ||
                  /RAND\(\)|ALEA\(\)/i.test(formula) ||
                  /OFFSET\(\)|DECALER\(\)/i.test(formula) ||
                  /INDIRECT\(\)/i.test(formula);

                if (!isVolatile) continue;

                // Convert formula to a static value (removes the formula -> reduces formulaCount)
                const newValue = valuesGrid[i]?.[j];
                const cell = usedRange.getCell(i, j);
                cell.load('address');
                await context.sync();
                const cellAddress = cell.address;

                if (allChanges.length < changesLimit) {
                  allChanges.push({
                    sheet: sheet.name,
                    cell: cellAddress,
                    before: formula,
                    after: newValue === null || newValue === undefined ? '' : String(newValue),
                  });
                }

                cell.values = [[newValue]];
                totalCount++;
              }
            }

            await context.sync();
          } catch (sheetError) {
            console.log(`  ⚠️ Impossible de traiter la feuille ${sheet.name}`);
          }
        }

        console.log(`✅ Total: ${totalCount} fonction(s) volatile(s) remplacée(s)`);
        
        // Créer un rapport détaillé
        let details = `${totalCount} fonction(s) volatile(s) remplacée(s)\n\n`;
        if (allChanges.length > 0) {
          details += 'Détails des modifications:\n';
          allChanges.slice(0, 10).forEach((change) => {
            details += `\n${change.sheet}!${change.cell}:\n  Avant: ${change.before}\n  Après: ${change.after}`;
          });
          if (allChanges.length > 10) {
            details += `\n\n... et ${allChanges.length - 10} autres modifications`;
          }
        }

        resolve({
          success: true,
          message: `${totalCount} fonction(s) volatile(s) remplacée(s)`,
          affectedCells: totalCount,
          details: details,
          changes: allChanges,
          errors: [],
        });
      } catch (error) {
        console.error('❌ Erreur:', error);
        reject(error);
      }
    }).catch(reject);
  });
}

/**
 * Simplifie les chaînes de formules
 * Scanne TOUTES les feuilles du classeur
 */
async function simplifyFormulaChains(): Promise<OptimizationResult> {
  return new Promise((resolve, reject) => {
    Excel.run(async (context) => {
      try {
        console.log('📊 Scan de toutes les feuilles...');
        const sheets = context.workbook.worksheets;
        sheets.load('items');
        await context.sync();

        let totalCount = 0;
        const allChanges: Array<{ sheet: string; cell: string; before: string; after: string }> = [];

        for (let sheetIdx = 0; sheetIdx < sheets.items.length; sheetIdx++) {
          const sheet = sheets.items[sheetIdx];
          console.log(`  Feuille ${sheetIdx + 1}/${sheets.items.length}: ${sheet.name}`);

          if (targetSheets && !targetSheets.has(String(sheet.name).toLowerCase())) {
            continue;
          }

          try {
            const usedRange = sheet.getUsedRange();
            usedRange.load('formulas');
            usedRange.load('address');
            await context.sync();

            const formulas = usedRange.formulas;

            for (let i = 0; i < formulas.length; i++) {
              for (let j = 0; j < formulas[i].length; j++) {
                const formula = String(formulas[i][j]);
                if (!formula.startsWith('=')) continue;

                let newFormula = formula;
                let modified = false;

                // Normaliser les espaces
                const normalized = newFormula.replace(/\s+/g, ' ').trim();
                if (normalized !== newFormula) {
                  newFormula = normalized;
                  modified = true;
                }

                // Supprimer les $ inutiles
                if (newFormula.includes('$')) {
                  const simplified = newFormula.replace(/\$([A-Z]+)\$(\d+)/g, '$1$2');
                  if (simplified !== newFormula) {
                    newFormula = simplified;
                    modified = true;
                  }
                }

                if (modified) {
                  const cell = usedRange.getCell(i, j);
                  cell.load('address');
                  await context.sync();
                  
                  const cellAddress = cell.address;
                  allChanges.push({
                    sheet: sheet.name,
                    cell: cellAddress,
                    before: formula,
                    after: newFormula,
                  });
                  
                  cell.formulas = [[newFormula]];
                  totalCount++;
                }
              }
            }

            await context.sync();
          } catch (sheetError) {
            console.log(`  ⚠️ Impossible de traiter la feuille ${sheet.name}`);
          }
        }

        console.log(`✅ Total: ${totalCount} formule(s) standardisée(s)`);
        
        // Créer un rapport détaillé
        let details = `${totalCount} formule(s) standardisée(s)\n\n`;
        if (allChanges.length > 0) {
          details += 'Détails des modifications:\n';
          allChanges.slice(0, 10).forEach((change) => {
            details += `\n${change.sheet}!${change.cell}:\n  Avant: ${change.before}\n  Après: ${change.after}`;
          });
          if (allChanges.length > 10) {
            details += `\n\n... et ${allChanges.length - 10} autres modifications`;
          }
        }

        resolve({
          success: true,
          message: `${totalCount} formule(s) standardisée(s)`,
          affectedCells: totalCount,
          details: details,
          changes: allChanges,
          errors: [],
        });
      } catch (error) {
        console.error('❌ Erreur:', error);
        reject(error);
      }
    }).catch(reject);
  });
}

/**
 * Limite les formules de colonne complète
 */
async function limitFullColumnFormulas(): Promise<OptimizationResult> {
  return new Promise((resolve, reject) => {
    Excel.run(async (context) => {
      try {
        const sheets = context.workbook.worksheets;
        sheets.load('items');
        await context.sync();

        let totalCount = 0;
        const allChanges: Array<{ sheet: string; cell: string; before: string; after: string; type?: 'value' | 'fill' }> = [];

        for (let sheetIdx = 0; sheetIdx < sheets.items.length; sheetIdx++) {
          const sheet = sheets.items[sheetIdx];

          if (targetSheets && !targetSheets.has(String(sheet.name).toLowerCase())) continue;

          try {
            const usedRange = sheet.getUsedRange();
            usedRange.load('formulas');
            await context.sync();

            const formulas = usedRange.formulas;

            for (let i = 0; i < formulas.length; i++) {
              for (let j = 0; j < formulas[i].length; j++) {
                const formula = String(formulas[i][j]);
                if (/[A-Z]:[A-Z]/.test(formula)) {
                  const newFormula = formula.replace(/([A-Z]):([A-Z])/g, '$1$21:$1$1000');
                  const cell = usedRange.getCell(i, j);
                  cell.load('address');
                  await context.sync();

                  allChanges.push({ sheet: sheet.name, cell: cell.address, before: formula, after: newFormula });
                  cell.formulas = [[newFormula]];
                  totalCount++;
                }
              }
            }

            await context.sync();
          } catch { /* skip sheet */ }
        }

        resolve({
          success: true,
          message: `${totalCount} référence(s) de colonne complète limitée(s)`,
          affectedCells: totalCount,
          changes: allChanges,
          errors: [],
        });
      } catch (error) {
        reject(error);
      }
    }).catch(reject);
  });
}

/**
 * Consolide les calculs dupliqués
 */
async function consolidateDuplicateCalculations(): Promise<OptimizationResult> {
  return new Promise((resolve, reject) => {
    Excel.run(async (context) => {
      try {
        const sheets = context.workbook.worksheets;
        sheets.load('items');
        await context.sync();

        let totalDuplicates = 0;
        let duplicateFormulas = 0;
        const allChanges: Array<{ sheet: string; cell: string; before: string; after: string; type?: 'value' | 'fill' }> = [];

        for (let sheetIdx = 0; sheetIdx < sheets.items.length; sheetIdx++) {
          const sheet = sheets.items[sheetIdx];

          if (targetSheets && !targetSheets.has(String(sheet.name).toLowerCase())) continue;

          try {
            const usedRange = sheet.getUsedRange();
            usedRange.load('formulas');
            await context.sync();

            const formulas = usedRange.formulas;
            const formulaMap = new Map<string, Array<{ row: number; col: number }>>();

            for (let i = 0; i < formulas.length; i++) {
              for (let j = 0; j < formulas[i].length; j++) {
                const formula = String(formulas[i][j]);
                if (formula.startsWith('=')) {
                  if (!formulaMap.has(formula)) formulaMap.set(formula, []);
                  formulaMap.get(formula)!.push({ row: i, col: j });
                }
              }
            }

            for (const [, positions] of formulaMap) {
              if (positions.length > 1) {
                for (let k = 1; k < positions.length; k++) {
                  const cell = usedRange.getCell(positions[k].row, positions[k].col);
                  cell.load('address');
                  await context.sync();
                  allChanges.push({ sheet: sheet.name, cell: cell.address, before: 'none', after: 'FFE0E0', type: 'fill' });
                  cell.format.fill.color = 'FFE0E0';
                  totalDuplicates++;
                }
                duplicateFormulas++;
              }
            }

            await context.sync();
          } catch { /* skip sheet */ }
        }

        resolve({
          success: true,
          message: `${duplicateFormulas} formule(s) dupliquée(s) (${totalDuplicates} occurrence(s) surlignée(s))`,
          affectedCells: totalDuplicates,
          changes: allChanges,
          errors: [],
        });
      } catch (error) {
        reject(error);
      }
    }).catch(reject);
  });
}

/**
 * Nettoie les données manquantes
 * Mode rapport uniquement : identifie et compte les cellules vides sans modifier le classeur
 */
async function cleanMissingData(): Promise<OptimizationResult> {
  return new Promise((resolve, reject) => {
    Excel.run(async (context) => {
      try {
        const sheets = context.workbook.worksheets;
        sheets.load('items');
        await context.sync();

        let totalIssues = 0;
        const allChanges: Array<{ sheet: string; cell: string; before: string; after: string }> = [];
        const changesLimit = 120;

        for (let sheetIdx = 0; sheetIdx < sheets.items.length; sheetIdx++) {
          const sheet = sheets.items[sheetIdx];

          if (targetSheets && !targetSheets.has(String(sheet.name).toLowerCase())) continue;

          try {
            const usedRange = sheet.getUsedRange();
            usedRange.load('values');
            await context.sync();

            const values = usedRange.values as unknown[][];
            const rowCount = values.length;
            const colCount = rowCount > 0 ? (values[0] as unknown[]).length : 0;

            const startRow = rowCount > 1 ? 1 : 0;
            for (let i = startRow; i < rowCount; i++) {
              for (let j = 0; j < colCount; j++) {
                const value = (values[i] as unknown[])[j];
                const isEmpty = value === null || value === undefined || String(value).trim() === '';
                if (!isEmpty) continue;

                totalIssues++;

                if (allChanges.length < changesLimit) {
                  const cell = usedRange.getCell(i, j);
                  cell.load('address');
                  await context.sync();
                  allChanges.push({ sheet: sheet.name, cell: cell.address, before: '(vide)', after: '(identifié)' });
                }
              }
            }
          } catch { /* skip sheet */ }
        }

        let details = `${totalIssues} cellule(s) vide(s) détectée(s) (aucune modification appliquée)\n\n`;
        if (allChanges.length > 0) {
          details += 'Emplacements:\n';
          allChanges.slice(0, 20).forEach((change) => {
            details += `  - ${change.sheet}!${change.cell}\n`;
          });
          if (allChanges.length > 20) {
            details += `  ... et ${allChanges.length - 20} autres`;
          }
        }

        resolve({
          success: true,
          message: `${totalIssues} cellule(s) vide(s) détectée(s) — rapport uniquement, aucune modification`,
          affectedCells: totalIssues,
          details,
          changes: [], // Report-only, no actual changes to undo
          errors: [],
        });
      } catch (error) {
        reject(error);
      }
    }).catch(reject);
  });
}

/**
 * Supprime les colonnes vides du classeur de manière sécurisée.
 *
 * Règles de sécurité :
 * 1. Ignore les feuilles de type dashboard/tableau de bord (graphiques, nom évocateur)
 * 2. Ne supprime que les colonnes vides CONSÉCUTIVES en fin de zone (trailing)
 *    → les colonnes vides intercalées entre des données (espaceurs) sont préservées
 * 3. Supprime de droite à gauche pour éviter le décalage d'index
 */
async function removeEmptyColumns(): Promise<OptimizationResult> {
  return new Promise((resolve, reject) => {
    Excel.run(async (context) => {
      try {
        const sheets = context.workbook.worksheets;
        sheets.load('items');
        await context.sync();

        let totalDeleted = 0;
        const skippedSheets: string[] = [];
        const allChanges: Array<{ sheet: string; cell: string; before: string; after: string; type?: 'value' | 'fill' }> = [];

        for (let sheetIdx = 0; sheetIdx < sheets.items.length; sheetIdx++) {
          const sheet = sheets.items[sheetIdx];

          if (targetSheets && !targetSheets.has(String(sheet.name).toLowerCase())) continue;

          // Skip dashboard-type sheets: check for charts
          const charts = sheet.charts;
          charts.load('count');
          await context.sync();

          const nameLower = sheet.name.toLowerCase();
          const isDashboard =
            charts.count > 0 ||
            nameLower.includes('dashboard') ||
            nameLower.includes('tableau de bord') ||
            nameLower.includes('kpi') ||
            nameLower.includes('résumé') ||
            nameLower.includes('resume');

          if (isDashboard) {
            skippedSheets.push(sheet.name);
            continue;
          }

          try {
            const usedRange = sheet.getUsedRangeOrNullObject();
            usedRange.load(['values', 'columnCount']);
            await context.sync();

            if (usedRange.isNullObject) continue;

            const values = usedRange.values as unknown[][];
            const columnCount = (values[0] as unknown[] | undefined)?.length ?? 0;

            // Find trailing empty columns (consecutive from the right edge)
            const trailingEmpty: number[] = [];
            for (let col = columnCount - 1; col >= 0; col--) {
              let isEmpty = true;
              for (let row = 0; row < values.length; row++) {
                if (values[row][col] !== null && values[row][col] !== undefined && values[row][col] !== '') {
                  isEmpty = false;
                  break;
                }
              }
              if (isEmpty) {
                trailingEmpty.push(col);
              } else {
                break; // Stop at the first non-empty column
              }
            }

            // Delete trailing empty columns (already ordered right-to-left)
            for (const colIdx of trailingEmpty) {
              const colRange = usedRange.getColumn(colIdx);
              const entireCol = colRange.getEntireColumn();
              entireCol.load('address');
              await context.sync();

              allChanges.push({
                sheet: sheet.name,
                cell: entireCol.address,
                before: 'column',
                after: 'deleted',
                type: 'value',
              });

              entireCol.delete(Excel.DeleteShiftDirection.left);
              totalDeleted++;
            }

            if (trailingEmpty.length > 0) {
              await context.sync();
            }
          } catch { /* skip sheet */ }
        }

        let msg = `${totalDeleted} colonne(s) vide(s) supprimée(s) (colonnes en fin de zone uniquement)`;
        if (skippedSheets.length > 0) {
          msg += `. Feuilles ignorées (dashboard/KPI): ${skippedSheets.join(', ')}`;
        }

        resolve({
          success: true,
          message: msg,
          affectedCells: totalDeleted,
          changes: allChanges,
          errors: [],
        });
      } catch (error) {
        reject(error);
      }
    }).catch(reject);
  });
}

/**
 * Améliore la qualité des données (trim/normalize)
 */
async function improveDataQuality(): Promise<OptimizationResult> {
  return new Promise((resolve, reject) => {
    Excel.run(async (context) => {
      try {
        const sheets = context.workbook.worksheets;
        sheets.load('items');
        await context.sync();

        let totalCleaned = 0;
        const allChanges: Array<{ sheet: string; cell: string; before: string; after: string; type?: 'value' | 'fill' }> = [];

        for (let sheetIdx = 0; sheetIdx < sheets.items.length; sheetIdx++) {
          const sheet = sheets.items[sheetIdx];

          if (targetSheets && !targetSheets.has(String(sheet.name).toLowerCase())) continue;

          try {
            const usedRange = sheet.getUsedRange();
            usedRange.load('values');
            await context.sync();

            const values = usedRange.values as unknown[][];
            const rowCount = values.length;
            const colCount = rowCount > 0 ? (values[0] as unknown[]).length : 0;

            const startRow = rowCount > 1 ? 1 : 0;
            for (let i = startRow; i < rowCount; i++) {
              for (let j = 0; j < colCount; j++) {
                const value = (values[i] as unknown[])[j];
                const isEmpty = value === null || value === undefined || String(value).trim() === '';
                if (isEmpty) continue;

                if (typeof value === 'string') {
                  const normalized = value.trim().replace(/\s+/g, ' ');
                  if (normalized !== value) {
                    const cell = usedRange.getCell(i, j);
                    cell.load('address');
                    await context.sync();
                    allChanges.push({ sheet: sheet.name, cell: cell.address, before: value, after: normalized });
                    cell.values = [[normalized]];
                    totalCleaned++;
                  }
                }
              }
            }

            await context.sync();
          } catch { /* skip sheet */ }
        }

        resolve({
          success: true,
          message: `${totalCleaned} cellule(s) nettoyée(s)`,
          affectedCells: totalCleaned,
          changes: allChanges,
          errors: [],
        });
      } catch (error) {
        reject(error);
      }
    }).catch(reject);
  });
}
